import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, TrendingUp, Users } from "lucide-react";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  user_id: string;
  price: number;
  status: string;
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface CoachAnalyticsProps {
  bookings: Booking[];
}

export const CoachAnalytics = ({ bookings }: CoachAnalyticsProps) => {
  const customerData = bookings.reduce((acc, booking) => {
    const userId = booking.user_id;
    if (!acc[userId]) {
      acc[userId] = {
        name: booking.profiles?.full_name || "Unknown",
        email: booking.profiles?.email || "",
        totalLessons: 0,
        totalSpent: 0,
        confirmedLessons: 0,
      };
    }
    acc[userId].totalLessons++;
    acc[userId].totalSpent += Number(booking.price);
    if (booking.status === "confirmed") {
      acc[userId].confirmedLessons++;
    }
    return acc;
  }, {} as Record<string, { name: string; email: string; totalLessons: number; totalSpent: number; confirmedLessons: number }>);

  const customers = Object.values(customerData).sort((a, b) => b.totalSpent - a.totalSpent);

  const totalRevenue = bookings
    .filter(b => b.status === "confirmed")
    .reduce((sum, b) => sum + Number(b.price), 0);

  const totalCustomers = customers.length;
  const avgRevenuePerCustomer = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Total Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCustomers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Avg per Customer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${avgRevenuePerCustomer.toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Table */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Analytics</CardTitle>
          <CardDescription>
            Detailed breakdown of all your customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Total Lessons</TableHead>
                <TableHead className="text-right">Confirmed</TableHead>
                <TableHead className="text-right">Total Spent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No customer data available
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{customer.name}</TableCell>
                    <TableCell className="text-muted-foreground">{customer.email}</TableCell>
                    <TableCell className="text-right">{customer.totalLessons}</TableCell>
                    <TableCell className="text-right">{customer.confirmedLessons}</TableCell>
                    <TableCell className="text-right font-semibold">
                      ${customer.totalSpent.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
